'use client'

import { useEffect, useMemo, useState } from 'react'

import { CheckCircle2 } from 'lucide-react'

import {
  parseFinishError,
  useFinishEvaluation,
  useSaveAnswer
} from '../hooks/useEvaluationAnswers'
import { useEvaluation } from '../hooks/useEvaluations'
import {
  answersFromRecords,
  isBlankAnswer,
  pendingRequiredQuestions,
  type AnswerMap
} from '../lib/answer-state'
import { countQuestions, KIND_BADGE } from '../lib/evaluation-ui'
import type {
  EvaluationAnswerValue,
  PendingQuestion
} from '../types/evaluation'

import { cn } from '@/shared/lib/utils'
import { Button } from '@/shared/ui/Button'
import { Card, CardBody, CardHeader, CardTitle } from '@/shared/ui/Card'
import { Page } from '@/shared/ui/Page'
import { StatusScreen } from '@/shared/ui/StatusScreen'

import { QuestionField } from './QuestionField'

interface EvaluationAnswerPageProps {
  evaluationId: string
}

export function EvaluationAnswerPage({
  evaluationId
}: EvaluationAnswerPageProps) {
  const { evaluation, isLoading, isError } = useEvaluation(evaluationId)
  const saveAnswer = useSaveAnswer(evaluationId)
  const finishEvaluation = useFinishEvaluation(evaluationId)

  const [answers, setAnswers] = useState<AnswerMap>({})
  const [pendingQuestions, setPendingQuestions] = useState<PendingQuestion[]>([])
  const [finishMessage, setFinishMessage] = useState<string | null>(null)

  // As respostas já gravadas hidratam o formulário — é o que faz a retomada.
  useEffect(() => {
    if (!evaluation) return
    setAnswers(answersFromRecords(evaluation.my_answers))
  }, [evaluation])

  const readOnly = !!evaluation?.finished

  const localPending = useMemo(
    () =>
      evaluation ? pendingRequiredQuestions(evaluation.questions, answers) : [],
    [evaluation, answers]
  )

  // Progresso é sobre o que o aluno respondeu, não sobre o que falta: ver a
  // barra andar é o que sustenta um questionário longo.
  const progress = useMemo(() => {
    if (!evaluation) return { answered: 0, total: 0, percentage: 0 }

    const answerable = evaluation.questions.filter(q => q.kind !== 'heading')
    const answered = answerable.filter(q => !isBlankAnswer(answers[q.id])).length

    return {
      answered,
      total: answerable.length,
      percentage: answerable.length ? (answered / answerable.length) * 100 : 0
    }
  }, [evaluation, answers])

  if (isLoading)
    return (
      <StatusScreen
        variant="purple"
        title="Carregando avaliação"
        description="Só um instante."
      />
    )

  if (isError || !evaluation)
    return (
      <StatusScreen
        variant="purple"
        title="Avaliação indisponível"
        description="Ela pode ter sido encerrada ou não estar liberada para você."
      />
    )

  const badge = KIND_BADGE[evaluation.kind]

  const handleChange = (questionId: number, value: EvaluationAnswerValue) => {
    setAnswers(current => ({ ...current, [questionId]: value }))
    setPendingQuestions(current =>
      current.filter(question => question.id !== questionId)
    )
    saveAnswer.mutate({ questionId, value })
  }

  const handleFinish = () => {
    setFinishMessage(null)
    finishEvaluation.mutate(undefined, {
      onSuccess: () => setPendingQuestions([]),
      onError: error => {
        const parsed = parseFinishError(error)
        setPendingQuestions(parsed.pendingQuestions)
        setFinishMessage(parsed.message)

        // Levar o aluno até a primeira pendência: numa avaliação longa, dizer
        // "faltam 3" sem mostrar quais é o mesmo que não dizer nada.
        const first = parsed.pendingQuestions[0]
        if (first)
          document
            .getElementById(`question-${first.id}`)
            ?.scrollIntoView({ behavior: 'smooth', block: 'center' })
      }
    })
  }

  let questionNumber = 0

  return (
    <Page variant="purple">
      <Card variant="purple">
        <CardHeader>
          <CardTitle>{evaluation.name}</CardTitle>
        </CardHeader>
        <CardBody>
          <div className="flex flex-wrap items-center gap-2">
            <span
              className={cn(
                'block rounded-sm px-1 text-xs font-normal text-white',
                badge.className
              )}
            >
              {badge.label}
            </span>
            <span className="text-xs font-medium text-[#7F7FAD]">
              {countQuestions(evaluation)} perguntas
              {evaluation.estimated_time ? ` · ${evaluation.estimated_time}` : ''}
            </span>
          </div>

          {evaluation.instructions ? (
            <p
              className="text-sm font-medium text-[#434368]"
              style={{ overflowWrap: 'anywhere' }}
            >
              {evaluation.instructions}
            </p>
          ) : null}

          {readOnly ? (
            <div className="flex items-center gap-2 rounded-xl bg-guaco/15 px-4 py-3 text-sm font-medium text-[#1F6B45]">
              <CheckCircle2 className="size-5 shrink-0" aria-hidden />
              <span>
                Você já enviou esta avaliação. Abaixo estão as suas respostas.
              </span>
            </div>
          ) : (
            <div
              className="relative h-4 w-full rounded-sm bg-[#50507C] text-center font-sans text-xs text-white"
              role="progressbar"
              aria-valuenow={progress.answered}
              aria-valuemin={0}
              aria-valuemax={progress.total}
              aria-label="Perguntas respondidas"
            >
              <div
                className="absolute top-0 left-0 h-full rounded-sm bg-primary transition-all duration-200"
                style={{ width: `${progress.percentage}%` }}
              />
              <span className="absolute top-0 left-0 flex h-full w-full items-center justify-center">
                {progress.answered}/{progress.total}
              </span>
            </div>
          )}

          <div className="flex flex-col gap-5">
            {evaluation.questions.map(question => {
              if (question.kind === 'heading')
                return (
                  <h2
                    key={question.id}
                    className="font-hl border-b border-[#D5D2FF] pt-2 pb-2 text-base font-bold text-primary-dark"
                    style={{ overflowWrap: 'anywhere' }}
                  >
                    {question.name}
                  </h2>
                )

              questionNumber += 1
              const isPending = pendingQuestions.some(
                pending => pending.id === question.id
              )

              return (
                <div
                  key={question.id}
                  id={`question-${question.id}`}
                  className={cn(
                    'flex flex-col gap-3 rounded-xl p-3 transition-all duration-100',
                    isPending && 'bg-begonia/10 ring-2 ring-begonia'
                  )}
                >
                  <div className="flex gap-2">
                    <span className="font-hl shrink-0 text-sm font-bold text-[#7F7FAD]">
                      {questionNumber}.
                    </span>
                    <p
                      className="font-medium text-primary-dark"
                      style={{ overflowWrap: 'anywhere' }}
                    >
                      {question.name}
                      {question.required ? (
                        <span
                          className="ml-1 text-begonia"
                          aria-label="obrigatória"
                        >
                          *
                        </span>
                      ) : null}
                    </p>
                  </div>

                  <QuestionField
                    question={question}
                    value={answers[question.id]}
                    readOnly={readOnly}
                    onChange={value => handleChange(question.id, value)}
                  />

                  {isPending ? (
                    <p className="text-sm font-medium text-begonia">
                      Esta pergunta é obrigatória.
                    </p>
                  ) : null}
                </div>
              )
            })}
          </div>

          {!readOnly ? (
            <div className="flex flex-col gap-3 border-t border-[#D5D2FF] pt-4">
              {finishMessage ? (
                <p className="text-sm font-medium text-begonia">
                  {finishMessage}
                </p>
              ) : null}
              {localPending.length > 0 ? (
                <p className="text-sm font-medium text-[#7F7FAD]">
                  {localPending.length === 1
                    ? 'Falta 1 pergunta obrigatória.'
                    : `Faltam ${localPending.length} perguntas obrigatórias.`}
                </p>
              ) : null}
              <Button
                variant="primary"
                onClick={handleFinish}
                loading={finishEvaluation.isPending}
                disabled={finishEvaluation.isPending}
              >
                Enviar respostas
              </Button>
            </div>
          ) : null}
        </CardBody>
      </Card>
    </Page>
  )
}
